import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserX, Trash2, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConditionItem } from "./ConditionItem";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

import type { SilaeFormData, SilaeCondition } from "@/types/silae";
import type { FormikProps } from "formik";
// Removed dashed add area, using button like in BonusConfig

interface ExclusionConfigProps {
  formik: FormikProps<SilaeFormData>;
  className?: string;
}

/**
 * Génère un ID unique pour une nouvelle exclusion
 */
const generateExclusionId = (): string => {
  return `exclusion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Composant de configuration des exclusions de personnel
 * Réutilise le composant ConditionItem pour la cohérence
 */
export const ExclusionConfig: React.FC<ExclusionConfigProps> = ({
  formik,
  className = "",
}) => {
  const { t } = useTranslation();
  const exclusions = useMemo(
    () => formik.values.exclusions || [],
    [formik.values.exclusions],
  );
  const hasExclusions = formik.values.hasExclusions;
  const [activeTab, setActiveTab] = useState("exclusion-0");

  // Ajouter une nouvelle exclusion
  const addExclusion = useCallback(() => {
    const newExclusion: SilaeCondition = {
      id: generateExclusionId(),
      type: "VAR_SALARY_BASE",
      silaeVariable: "bul.salairedebase",
      conditionOperator: ">=",
      conditionValue: "",
    };

    const updatedExclusions = [...exclusions, newExclusion];
    formik.setFieldValue("exclusions", updatedExclusions);

    // Ajouter automatiquement "AND" au tableau exclusionsOperators si plusieurs exclusions
    if (updatedExclusions.length > 1) {
      const currentOps = ((
        formik.values as unknown as Record<string, string[]>
      )["exclusionsOperators"] || []) as string[];
      const updatedOps = [...currentOps, "AND"];
      // S'assurer que le tableau a la bonne longueur (n-1 pour n exclusions)
      while (updatedOps.length < updatedExclusions.length - 1) {
        updatedOps.push("AND");
      }
      formik.setFieldValue("exclusionsOperators", updatedOps);
    }

    setActiveTab(`exclusion-${updatedExclusions.length - 1}`);
  }, [exclusions, formik]);

  // Supprimer une exclusion
  const removeExclusion = useCallback(
    (index: number) => {
      const updatedExclusions = exclusions.filter((_, i) => i !== index);
      formik.setFieldValue("exclusions", updatedExclusions);

      // Ajuster les opérateurs si nécessaire (utilise exclusionsOperators si présent)
      const ops = ((formik.values as unknown as Record<string, string[]>)[
        "exclusionsOperators"
      ] || []) as string[];
      if (ops.length > 0 && updatedExclusions.length > 1) {
        const updatedOps: string[] = [];
        for (let i = 0; i < index && i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        for (let i = index + 1; i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        while (updatedOps.length < updatedExclusions.length - 1) {
          updatedOps.push("AND");
        }
        formik.setFieldValue("exclusionsOperators", updatedOps);
      } else if (updatedExclusions.length <= 1) {
        formik.setFieldValue("exclusionsOperators", []);
      }

      // Ajuster l'onglet actif
      if (activeTab === `exclusion-${index}`) {
        const newIndex = Math.max(0, index - 1);
        setActiveTab(`exclusion-${newIndex}`);
      } else {
        const currentTabIndex = parseInt(activeTab.split("-")[1] || "0");
        if (currentTabIndex > index) {
          setActiveTab(`exclusion-${currentTabIndex - 1}`);
        }
      }
    },
    [exclusions, formik, activeTab],
  );

  // Activer/désactiver les exclusions
  const toggleExclusions = useCallback(
    (enabled: boolean) => {
      formik.setFieldValue("hasExclusions", enabled);
      if (!enabled) {
        formik.setFieldValue("exclusions", []);
        formik.setFieldValue("exclusionsOperators", []);
      } else {
        // Si on active les exclusions et qu'il n'y en a aucune, créer une première exclusion
        if (exclusions.length === 0) {
          const newExclusion: SilaeCondition = {
            id: generateExclusionId(),
            type: "VAR_SALARY_BASE",
            silaeVariable: "bul.salairedebase",
            conditionOperator: "<=",
            conditionValue: "",
            conditionPercentage: "100",
          };
          formik.setFieldValue("exclusions", [newExclusion]);
          formik.setFieldValue("exclusionsOperators", []);
          setActiveTab("exclusion-0");
        }
      }
      setTimeout(() => {
        formik.setFieldTouched("exclusions", true);
        formik.setFieldTouched("exclusions.0", true);
        formik.setFieldTouched("exclusions.0.conditionValue", true);

        // Déclencher la validation pour que les erreurs apparaissent immédiatement
        formik.validateForm();
      }, 0);
    },
    [formik, exclusions.length],
  );

  return (
    <Card className={className}>
      <CardHeader className="flex mb-0 items-center p-4 mx-6 rounded-lg  bg-muted/50 justify-between">
        <div className="flex flex-col  justify-between">
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t("wizard.structure.exclusions.title")}
            </CardTitle>
          </div>
          <CardDescription>
            {t("wizard.structure.exclusions.description")}
          </CardDescription>
        </div>
        <Switch
          id="hasExclusions"
          checked={hasExclusions}
          onCheckedChange={toggleExclusions}
        />
      </CardHeader>
      {hasExclusions && (
        <CardContent className="space-y-4">
          {/* Configuration des exclusions si activées */}

          <div className="space-y-4">
            {/* Liste des exclusions */}
            {exclusions.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">
                    {t("wizard.structure.exclusions.conditions")}
                  </Label>
                  <Badge variant="outline" className="text-xs">
                    {exclusions.length}{" "}
                    {exclusions.length > 1
                      ? t("wizard.structure.exclusions.conditionsPlural")
                      : t("wizard.structure.exclusions.condition")}
                  </Badge>
                </div>

                {/* Barre opérateurs + bouton d'ajout (comme BonusConfig) */}
                <div className="flex items-center justify-between gap-2">
                  {exclusions.length > 1 && (
                    <div className="space-y-2 border-b pb-4">
                      <Label>{t("advancedConditions.logicalOperator")}</Label>
                      <div className="text-sm text-muted-foreground mb-2">
                        {t("advancedConditions.operatorBetweenConditions")}
                      </div>
                      <div className="flex flex-wrap gap-4 items-center">
                        {exclusions.slice(0, -1).map((_, index) => {
                          const ops = ((
                            formik.values as unknown as Record<string, string[]>
                          )["exclusionsOperators"] || []) as string[];
                          const current = ops[index] || "AND";
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <Badge variant="outline" className="text-xs">
                                {t("advancedConditions.condition")} {index + 1}
                              </Badge>
                              <Select
                                value={current}
                                onValueChange={(value) => {
                                  const next = [...ops];
                                  while (next.length < exclusions.length - 1)
                                    next.push("AND");
                                  next[index] = value;
                                  formik.setFieldValue(
                                    "exclusionsOperators",
                                    next,
                                  );
                                }}
                              >
                                <SelectTrigger className="w-fit min-w-[80px]">
                                  <SelectValue>
                                    {current === "AND" ? (
                                      <Badge variant="secondary">
                                        {t(
                                          "advancedConditions.operators.and.label",
                                        )}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline">
                                        {t(
                                          "advancedConditions.operators.or.label",
                                        )}
                                      </Badge>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="AND">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary">
                                        {t(
                                          "advancedConditions.operators.and.label",
                                        )}
                                      </Badge>
                                      <span>
                                        {t(
                                          "advancedConditions.operators.and.description",
                                        )}
                                      </span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="OR">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline">
                                        {t(
                                          "advancedConditions.operators.or.label",
                                        )}
                                      </Badge>
                                      <span>
                                        {t(
                                          "advancedConditions.operators.or.description",
                                        )}
                                      </span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <Badge variant="outline" className="text-xs">
                                {t("advancedConditions.condition")} {index + 2}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addExclusion}
                    className="w-fit"
                    disabled={exclusions.length >= 5}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("wizard.structure.exclusions.addCondition")}
                  </Button>
                </div>

                {/* Système de tabs pour éditer chaque exclusion */}
                {exclusions.length === 1 ? (
                  <ConditionItem
                    className="flex-1"
                    formik={
                      {
                        ...formik,
                        values: { ...formik.values, conditions: exclusions },
                        errors: {
                          ...formik.errors,
                          conditions: formik.errors.exclusions,
                        },
                        touched: {
                          ...formik.touched,
                          conditions: formik.touched.exclusions,
                        },
                        setFieldValue: (field: string, value: string) => {
                          if (field.startsWith("conditions[")) {
                            const exclusionField = field.replace(
                              "conditions[",
                              "exclusions[",
                            );
                            formik.setFieldValue(exclusionField, value);
                          } else if (field.startsWith("conditions.")) {
                            const exclusionField = field.replace(
                              "conditions.",
                              "exclusions.",
                            );
                            formik.setFieldValue(exclusionField, value);
                          } else {
                            formik.setFieldValue(field, value);
                          }
                        },
                        setFieldTouched: (field: string, touched?: boolean) => {
                          if (field.startsWith("conditions[")) {
                            const exclusionField = field.replace(
                              "conditions[",
                              "exclusions[",
                            );
                            formik.setFieldTouched(exclusionField, touched);
                          } else if (field.startsWith("conditions.")) {
                            const exclusionField = field.replace(
                              "conditions.",
                              "exclusions.",
                            );
                            formik.setFieldTouched(exclusionField, touched);
                          } else {
                            formik.setFieldTouched(field, touched);
                          }
                        },
                      } as FormikProps<SilaeFormData>
                    }
                    conditionIndex={0}
                    canDelete={false}
                    onDelete={() => removeExclusion(0)}
                  />
                ) : (
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
                      {exclusions.map((exclusion, index) => (
                        <div
                          key={exclusion.id}
                          className="flex items-center gap-1"
                        >
                          <TabsTrigger
                            value={`exclusion-${index}`}
                            className="flex items-center gap-2"
                          >
                            <UserX className="h-3 w-3" />
                            {t("advancedConditions.condition")} {index + 1}
                            {exclusions.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeExclusion(index);
                                }}
                                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash2 className="h-2 w-2" />
                              </Button>
                            )}
                          </TabsTrigger>
                        </div>
                      ))}
                    </TabsList>

                    {exclusions.map((_, index) => (
                      <TabsContent key={index} value={`exclusion-${index}`}>
                        <ConditionItem
                          formik={
                            {
                              ...formik,
                              values: {
                                ...formik.values,
                                conditions: exclusions,
                              },
                              errors: {
                                ...formik.errors,
                                conditions: formik.errors.exclusions,
                              },
                              touched: {
                                ...formik.touched,
                                conditions: formik.touched.exclusions,
                              },
                              setFieldValue: (field: string, value: string) => {
                                if (field.startsWith("conditions[")) {
                                  const exclusionField = field.replace(
                                    "conditions[",
                                    "exclusions[",
                                  );
                                  formik.setFieldValue(exclusionField, value);
                                } else if (field.startsWith("conditions.")) {
                                  const exclusionField = field.replace(
                                    "conditions.",
                                    "exclusions.",
                                  );
                                  formik.setFieldValue(exclusionField, value);
                                } else {
                                  formik.setFieldValue(field, value);
                                }
                              },
                              setFieldTouched: (
                                field: string,
                                touched?: boolean,
                              ) => {
                                if (field.startsWith("conditions[")) {
                                  const exclusionField = field.replace(
                                    "conditions[",
                                    "exclusions[",
                                  );
                                  formik.setFieldTouched(
                                    exclusionField,
                                    touched,
                                  );
                                } else if (field.startsWith("conditions.")) {
                                  const exclusionField = field.replace(
                                    "conditions.",
                                    "exclusions.",
                                  );
                                  formik.setFieldTouched(
                                    exclusionField,
                                    touched,
                                  );
                                } else {
                                  formik.setFieldTouched(field, touched);
                                }
                              },
                            } as FormikProps<SilaeFormData>
                          }
                          conditionIndex={index}
                          canDelete={exclusions.length > 1}
                          onDelete={() => removeExclusion(index)}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                )}

                {/* Zone d'ajout remplacée par un bouton (ci-dessus) */}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
